import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useResetPassword } from '../../api/hooks/useAuth.ts';
import {
  AuthCard,
  buttonClass,
  Field,
  FormError,
  FormNotice,
  inputClass,
} from '../../components/AuthCard.tsx';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const reset = useResetPassword();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const mismatch = confirm.length > 0 && password !== confirm;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (mismatch) return;
    reset.mutate({ token, password });
  }

  if (!token) {
    return (
      <AuthCard title="Reset your password">
        <FormError message="Missing reset token." />
        <Link className="block text-center text-sm text-blue-600 hover:underline" to="/forgot-password">
          Request a new link
        </Link>
      </AuthCard>
    );
  }

  if (reset.isSuccess) {
    return (
      <AuthCard title="Password changed">
        <FormNotice message="Your password is set and every other session has been signed out. You can sign in now." />
        <Link className="block text-center text-sm text-blue-600 hover:underline" to="/login">
          Go to sign in
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Choose a new password">
      <form onSubmit={onSubmit}>
        <FormError
          message={
            mismatch
              ? "The two passwords don't match."
              : reset.isError
                ? 'This reset link is invalid or has expired. Request a new one.'
                : undefined
          }
        />
        <Field label="New password (10+ characters)">
          <input
            className={inputClass}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
          />
        </Field>
        <Field label="Confirm new password">
          <input
            className={inputClass}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
          />
        </Field>
        <button className={buttonClass} type="submit" disabled={reset.isPending || mismatch}>
          {reset.isPending ? 'Saving…' : 'Set new password'}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-gray-500">
        Link expired?{' '}
        <Link className="text-blue-600 hover:underline" to="/forgot-password">
          Request a new one
        </Link>
      </p>
    </AuthCard>
  );
}
